import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { ApiService } from '../../../services/api.service';

interface ActivityItem {
  marketIcon: string;
  marketName: string;
  marketWindow: string;
  traderAvatar: string;
  traderName: string;
  action: string;
  actionColor: string;
  price: string;
  amount: string;
  timeAgo: string;
  txn_hash?: string;
}

@Component({
  selector: 'app-activity',
  imports: [CommonModule, NavBarComponent],
  templateUrl: './activity.component.html',
  styleUrl: './activity.component.css'
})
export class ActivityComponent implements OnInit {
  activities: ActivityItem[] = [];
  isLoading = false;
  marketList: any[] = [];
  currentPage = 1;
  itemsPerPage = 25;
  totalItems = 0;
  readonly pageGap = '...';
  /** Characters kept at the end of an address when the rest is ellipsised away */
  readonly addressTailLength = 4;

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    console.log('🚀 ActivityComponent initializing...');
    this.isLoading = true;
    this.loadMarkets();
  }

  loadMarkets(): void {
    // Fetch markets with empty filters
    console.log('Fetching markets list...');
    this.api.getMarketsList({ category_ids: [], tag_ids: [] }).subscribe({
      next: (res) => {
        console.log('Markets loaded:', res.data?.results?.length || 0, 'markets');
        this.marketList = res.data?.results || [];
        console.log('First few market IDs:', this.marketList.slice(0, 3).map((m: any) => m.id));
        this.loadActivityData();
      },
      error: (err) => {
        console.error('Failed to load markets:', err);
        this.isLoading = false;
      }
    });
  }

  loadActivityData(): void {
    if (!this.marketList || this.marketList.length === 0) {
      console.warn('No markets available');
      this.activities = [];
      this.isLoading = false;
      return;
    }

    // Limit to first 20 markets to avoid too many parallel requests
    const marketsToLoad = this.marketList.slice(0, 20);
    console.log(`Loading activity for ${marketsToLoad.length} markets (out of ${this.marketList.length} total)`);
    this.isLoading = true;
    
    // Create promises for each market's activity
    const activityPromises = marketsToLoad.map((market: any) => {
      console.log(`Requesting activity for market: ${market.id} (${market.name})`);
      return this.api.getMarketActivity(market.id, null, 1, 50).toPromise()
        .then((res: any) => {
          const count = res.data?.results?.length || 0;
          console.log(`✓ Activity loaded for market ${market.id}: ${count} items`);
          return { data: res, market };
        })
        .catch((err: any) => {
          console.error(`✗ Error loading activity for market ${market.id}:`, err.status, err.statusText, err.error?.detail);
          return { data: null, market };
        })
    });

    Promise.all(activityPromises).then((results) => {
      console.log('All activity promises resolved');
      const allActivities: ActivityItem[] = [];

      results.forEach((result: any) => {
        if (result.data?.data?.results && Array.isArray(result.data.data.results)) {
          result.data.data.results.forEach((item: any) => {
            const activity = this.mapApiActivityToItem(item, result.market);
            if (activity) {
              allActivities.push(activity);
            }
          });
        }
      });

      this.totalItems = allActivities.length;
      this.activities = allActivities;
      this.currentPage = 1;
      this.isLoading = false;
      console.log('✓ Activity component loaded with', this.activities.length, 'total items');
    }).catch((err) => {
      console.error('✗ Error in Promise.all:', err);
      this.isLoading = false;
    });
  }

  mapApiActivityToItem(apiItem: any, market: any): ActivityItem | null {
    try {
      const isBuy = apiItem.activity_text?.toLowerCase().includes('bought') || 
                    apiItem.activity_text?.toLowerCase().includes('buy');
      const actionColor = isBuy ? 'text-green-600' : 'text-red-500';
      
      // Extract amount and price from activity text or use defaults
      const parts = apiItem.activity_text?.split(' ') || [];
      const amount = parts[2] || 'N/A';
      const price = apiItem.activity_text?.match(/\$[\d.]+/) ? 
        apiItem.activity_text.match(/\$[\d.]+/)[0] : '$0.00';

      return {
        marketIcon: market.image_url || 'assets/images/deposit/btc.svg',
        marketName: market.name || apiItem.market_name || 'Market',
        marketWindow: new Date().toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        traderAvatar: apiItem.profile_picture || 'assets/images/home/profile-image.png',
        traderName: apiItem.activity_text?.split(' ')[0] || 'Trader',
        action: isBuy ? 'Yes' : 'No',
        actionColor,
        price,
        amount: `$${amount}`,
        timeAgo: apiItem.time_diff || 'Just now',
        txn_hash: apiItem.txn_hash
      };
    } catch (e) {
      console.error('Error mapping activity item:', e);
      return null;
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  get paginatedActivities(): ActivityItem[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.activities.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  /**
   * The narrow (991px and below) page list: the current page with a neighbour on
   * either side, then the last two pages, with anything in between collapsed
   * into an ellipsis. Page 3 of 20 gives `2 3 4 ... 19 20`, page 4 gives
   * `3 4 5 ... 19 20`, so the window slides while the tail stays put.
   */
  get compactPageNumbers(): (number | string)[] {
    const total = this.totalPages;

    // Short enough that the compact form would not hide anything
    if (total <= 5) {
      return this.pageNumbers;
    }

    // Keep the window three wide, clamped so it never runs past the tail pair
    const start = Math.max(1, Math.min(this.currentPage - 1, total - 2));
    const end = Math.min(start + 2, total);
    const pages: (number | string)[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    for (const page of [total - 1, total]) {
      if (page <= end) {
        continue; // the window already reaches this far
      }
      const distance = page - (pages[pages.length - 1] as number);
      if (distance === 2) {
        pages.push(page - 1); // only one page between — show it rather than an ellipsis
      } else if (distance > 2) {
        pages.push(this.pageGap);
      }
      pages.push(page);
    }

    return pages;
  }

  /**
   * The narrowest (575px and below) page list: the current page and the one
   * after it, then the last page, so the numbers step forward one at a time the
   * way they do on desktop. Page 1 of 20 gives `1 2 ... 20`, page 2 gives
   * `2 3 ... 20`, and the ellipsis disappears once the window reaches the tail.
   */
  get minimalPageNumbers(): (number | string)[] {
    const total = this.totalPages;

    // Nothing in the middle to collapse
    if (total <= 3) {
      return this.pageNumbers;
    }

    // Keep the window two wide, clamped so it never runs past the last page
    const start = Math.min(this.currentPage, total - 1);
    const pages: (number | string)[] = [start, start + 1];

    const distance = total - (start + 1);
    if (distance === 2) {
      pages.push(total - 1); // only one page between — show it rather than an ellipsis
    } else if (distance > 2) {
      pages.push(this.pageGap);
    }
    if (distance > 0) {
      pages.push(total);
    }

    return pages;
  }

  /**
   * Everything but the last few characters of an address. Paired with
   * `addressTail`, it lets CSS ellipsise the middle of the address rather than
   * its end, so the tail stays readable however little room is left.
   */
  addressHead(address: string): string {
    if (!address || address.length <= this.addressTailLength) {
      return address;
    }
    return address.slice(0, address.length - this.addressTailLength);
  }

  addressTail(address: string): string {
    if (!address || address.length <= this.addressTailLength) {
      return '';
    }
    return address.slice(-this.addressTailLength);
  }

  isPageGap(page: number | string): boolean {
    return page === this.pageGap;
  }

  asPage(page: number | string): number {
    return page as number;
  }
}
